import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardList, Loader2, Send } from "lucide-react";

/**
 * Guided interview links for a customer (DARCY-INTERVIEW-BOT.md
 * PRODUCTIZED): "Ask someone to fill this in" sends a magic-link
 * walkthrough via the interview_invite email recipe; finished interviews
 * surface their === BRAND === blocks here for review, and Approve STAGES
 * them for the future location-groups import — nothing auto-applies.
 */

interface InterviewLinkRow {
    id: string;
    kind: string;
    recipient_name: string;
    recipient_email: string;
    status: "active" | "completed" | "staged" | "revoked";
    created_at: string;
    brands_done: number;
    blocks_count: number;
}

interface LinkDetail {
    link: {
        id: string;
        recipient_name: string;
        status: string;
        structured_blocks: { brand: string; location_count: number; block: string }[];
        completed_brands: string[];
    };
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
    active: { label: "In progress", className: "bg-blue-100 text-blue-800" },
    completed: { label: "Finished — review", className: "bg-amber-100 text-amber-800" },
    staged: { label: "Staged for import", className: "bg-emerald-100 text-emerald-800" },
    revoked: { label: "Revoked", className: "bg-slate-100 text-slate-600" },
};

export function InterviewsCard({ customerId }: { customerId: string }) {
    const qc = useQueryClient();
    const [sendOpen, setSendOpen] = useState(false);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [kind, setKind] = useState("routing_collection");

    const linksQ = useQuery({
        queryKey: ["interview-links", customerId],
        queryFn: () => api.get<{ links: InterviewLinkRow[] }>(`/customers/${customerId}/interview-links`),
    });

    const detailQ = useQuery({
        queryKey: ["interview-link", reviewId],
        queryFn: () => api.get<LinkDetail>(`/interview-links/${reviewId}`),
        enabled: !!reviewId,
    });

    const createMut = useMutation({
        mutationFn: () =>
            api.post<{ id: string; url: string; emailed: boolean }>(`/customers/${customerId}/interview-links`, {
                recipient_name: name.trim(),
                recipient_email: email.trim(),
                kind,
            }),
        onSuccess: (data) => {
            toast.success(data.emailed ? `Walkthrough sent to ${email.trim()}` : "Link created — email didn't send, copy it manually");
            setSendOpen(false);
            setName("");
            setEmail("");
            qc.invalidateQueries({ queryKey: ["interview-links", customerId] });
        },
        onError: () => toast.error("Couldn't create the walkthrough link"),
    });

    const approveMut = useMutation({
        mutationFn: (linkId: string) => api.post(`/interview-links/${linkId}/approve`),
        onSuccess: () => {
            toast.success("Approved — staged for the location-groups import");
            setReviewId(null);
            qc.invalidateQueries({ queryKey: ["interview-links", customerId] });
        },
        onError: () => toast.error("Couldn't approve this interview"),
    });

    const links = linksQ.data?.links ?? [];

    return (
        <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Guided interviews
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setSendOpen(true)}>
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    Ask someone to fill this in
                </Button>
            </CardHeader>
            <CardContent>
                {linksQ.isLoading ? (
                    <div className="py-4 flex justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : links.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        No interviews yet. Send someone a guided walkthrough and their answers about who gets reports,
                        invoices, and quote approvals per brand land here for review.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {links.map((l) => {
                            const badge = STATUS_BADGE[l.status] ?? STATUS_BADGE.active;
                            return (
                                <div key={l.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm truncate">
                                            {l.recipient_name}
                                            <span className="text-muted-foreground font-normal"> · {l.recipient_email}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {l.brands_done} brand{l.brands_done === 1 ? "" : "s"} finished · sent{" "}
                                            {new Date(l.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Badge className={badge.className}>{badge.label}</Badge>
                                        {l.blocks_count > 0 && (
                                            <Button size="sm" variant="outline" onClick={() => setReviewId(l.id)}>
                                                Review
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>

            <Dialog open={sendOpen} onClose={() => setSendOpen(false)} title="Ask someone to fill this in">
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        Sends a magic-link walkthrough — no login, answers save as they go, resumable for weeks.
                    </p>
                    <div className="space-y-1.5">
                        <Label>Who's answering?</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Darcy" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Their email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="darcy@example.com"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Question wording</Label>
                        <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                            <option value="routing_collection">Our team (internal wording)</option>
                            <option value="customer_routing">Customer contact (plain language)</option>
                        </Select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setSendOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!name.trim() || !/.+@.+\..+/.test(email.trim()) || createMut.isPending}
                            onClick={() => createMut.mutate()}
                        >
                            {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send the walkthrough"}
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={!!reviewId}
                onClose={() => setReviewId(null)}
                title={`Interview — ${detailQ.data?.link.recipient_name ?? ""}`}
                className="max-w-2xl"
            >
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        Approving stages these blocks for the location-groups import. Nothing changes on the account
                        until that import runs.
                    </p>
                    {detailQ.isLoading ? (
                        <div className="py-6 flex justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {(detailQ.data?.link.structured_blocks ?? []).map((b) => (
                                <pre
                                    key={b.brand}
                                    className="whitespace-pre-wrap rounded-md bg-slate-50 border p-3 text-xs font-mono"
                                >
                                    {b.block}
                                </pre>
                            ))}
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setReviewId(null)}>
                            Close
                        </Button>
                        {detailQ.data && detailQ.data.link.status !== "staged" && (
                            <Button disabled={approveMut.isPending} onClick={() => reviewId && approveMut.mutate(reviewId)}>
                                {approveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve — stage for import"}
                            </Button>
                        )}
                    </div>
                </div>
            </Dialog>
        </Card>
    );
}
